import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { Loader2, Search, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MainLayout from "@/layouts/MainLayout";
import { Helmet } from "react-helmet-async";
import { Navigate } from "react-router-dom";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import { Input } from "@/components/ui/input";
import AnimatedSection from "@/components/AnimatedSection";
import LazyImage from "@/components/LazyImage";
import ParallaxHero from "@/components/ParallaxHero";

interface NewsArticle {
  id: string;
  title: string;
  slug: string;
  featured_image_url: string | null;
  category: string;
  content: string | null;
  published_at: string | null;
  created_at: string;
}

interface NewsCategory {
  id: string;
  name: string;
  slug: string;
  sort_order: number;
}

const stripHtml = (html: string) => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

const News = () => {
  const { settings, isLoading: settingsLoading } = useSiteSettings();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const { data: articles, isLoading } = useQuery({
    queryKey: ["all-news"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("news_articles")
        .select("id, title, slug, featured_image_url, category, content, published_at, created_at")
        .eq("is_published", true)
        .order("published_at", { ascending: false });

      if (error) throw error;
      return data as NewsArticle[];
    },
  });

  const { data: categories } = useQuery({
    queryKey: ["news-categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("news_categories")
        .select("*")
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return data as NewsCategory[];
    },
  });

  const { data: heroSetting } = useQuery({
    queryKey: ["site-setting", "news_hero_image"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("setting_value")
        .eq("setting_key", "news_hero_image")
        .maybeSingle();
      if (error) throw error;
      return data?.setting_value;
    },
  });

  // Calculate article counts per category
  const categoryCounts = useMemo(() => {
    if (!articles) return {};
    return articles.reduce(
      (acc, article) => {
        acc[article.category] = (acc[article.category] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );
  }, [articles]);

  const filteredArticles = useMemo(() => {
    if (!articles) return [];

    return articles.filter((article) => {
      const matchesSearch =
        searchQuery === "" ||
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (article.content && stripHtml(article.content).toLowerCase().includes(searchQuery.toLowerCase()));

      const matchesCategory = !selectedCategory || article.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [articles, searchQuery, selectedCategory]);

  // Use custom hero image, or fall back to first article's image
  const heroImage = heroSetting || articles?.[0]?.featured_image_url;

  if (!settingsLoading && settings.news_enabled === "false") {
    return <Navigate to="/" replace />;
  }

  return (
    <MainLayout>
      <Helmet>
        <title>News | Rogue Skull Games</title>
        <meta
          name="description"
          content="Latest news and updates from Rogue Skull Games. Stay informed about new games, events, and announcements."
        />
        <link rel="canonical" href="https://rogueskullgames.com/news" />
        <meta property="og:title" content="News | Rogue Skull Games" />
        <meta property="og:description" content="Announcements, dev logs, and event coverage from Rogue Skull Games." />
        <meta property="og:url" content="https://rogueskullgames.com/news" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Hero Section with Parallax */}
      <ParallaxHero imageUrl={heroImage} alt="News hero">
        {/* Content */}
        <div className="container hero-content z-20">
          <div className="">
            <div className="">
              <span className="inline-block text-blood-red font-semibold tracking-[0.3em] mb-4 animate-slide-in">
                LATEST NEWS
              </span>
              <h1
                className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-dark-foreground font-display font-bold leading-[1.1] mb-8 animate-fade-up"
                style={{ animationDelay: "0.1s" }}
              >
                Stay Updated
              </h1>
              <p
                className="text-dark-foreground/80 text-lg md:text-xl leading-relaxed max-w-2xl animate-fade-up"
                style={{ animationDelay: "0.2s" }}
              >
                Announcements, events, and community highlights from Rogue Skull Games. Be the first to know about new
                releases, conventions, and behind-the-scenes updates.
              </p>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-0 right-0 z-30 flex justify-center">
          <div className="flex flex-col items-center gap-2 animate-fade-up" style={{ animationDelay: "0.4s" }}>
            <span className="text-dark-foreground/80 text-xs tracking-[0.2em] uppercase">Scroll</span>
            <div className="w-px h-12 bg-gradient-to-b from-dark-foreground/40 to-transparent" />
          </div>
        </div>
      </ParallaxHero>

      {/* Filter Section */}
      {articles && articles.length > 0 && (
        <section className="section-dark py-6 md:py-8 border-b border-border">
          <div className="mx-auto px-4 md:px-12">
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 md:gap-6">
              <span className="text-muted-foreground text-sm uppercase tracking-wider hidden sm:block">Filter by:</span>

              <Select
                value={selectedCategory || "all"}
                onValueChange={(val) => setSelectedCategory(val === "all" ? null : val)}
              >
                <SelectTrigger className="w-full sm:w-[160px] bg-card border-border text-card-foreground">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories ({articles?.length || 0})</SelectItem>
                  {categories?.map((category) => (
                    <SelectItem key={category.id} value={category.slug}>
                      {category.name} ({categoryCounts[category.slug] || 0})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex items-center gap-3 sm:gap-4 sm:ml-auto w-full sm:w-auto">
                {(selectedCategory || searchQuery) && (
                  <button
                    onClick={() => {
                      setSelectedCategory(null);
                      setSearchQuery("");
                    }}
                    className="text-blood-red text-sm hover:underline whitespace-nowrap"
                  >
                    Clear
                  </button>
                )}

                <div className="relative flex-1 sm:flex-none">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-11 pr-10 w-full sm:w-[200px] md:w-[240px] bg-card border-border text-card-foreground placeholder:text-muted-foreground"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* All Articles List */}
      <section className="section-dark py-16">
        <div className="mx-auto px-6 md:px-12">
          <div className="max-w-7xl mx-auto">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : filteredArticles.length === 0 ? (
              <p className="text-center text-muted-foreground py-12">
                {articles && articles.length > 0 ? "No articles match your search." : "No articles yet."}
              </p>
            ) : (
              <div className="">
                {filteredArticles.map((article, index) => (
                  <AnimatedSection key={article.id} animation="fade-up" delay={index * 50} className="py-12">
                    <div className="grid lg:grid-cols-[2fr_3fr] gap-16 items-center">
                      {/* Article Image */}
                      <Link
                        to={`/news/${article.slug}`}
                        className="group relative aspect-[4/3] overflow-hidden block overflow-hidden"
                      >
                        {article.featured_image_url ? (
                          <LazyImage
                            src={article.featured_image_url}
                            alt={article.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          />
                        ) : (
                          <div className="w-full h-full bg-muted flex items-center justify-center">
                            <span className="text-muted-foreground text-sm uppercase tracking-wider">No Image</span>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-dark/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      </Link>

                      {/* Article Content */}
                      <div>
                        <span className="text-blood-red font-semibold tracking-[0.3em] uppercase text-xs">
                          {article.category}
                        </span>
                        <Link to={`/news/${article.slug}`}>
                          <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground uppercase tracking-wide mb-4 hover:text-blood-red transition-colors">
                            {article.title}
                          </h2>
                        </Link>
                        <p className="text-muted-foreground text-sm uppercase tracking-wider mb-4">
                          {format(new Date(article.published_at || article.created_at), "MMMM d, yyyy")}
                        </p>
                        {article.content && (
                          <p className="text-muted-foreground leading-relaxed mb-6 line-clamp-2">
                            {stripHtml(article.content).substring(0, 200)}...
                          </p>
                        )}
                        <Link
                          to={`/news/${article.slug}`}
                          className="btn-outline-light"
                        >
                          Read Article
                        </Link>
                      </div>
                    </div>
                  </AnimatedSection>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </MainLayout>
  );
};

export default News;
